import { useNavigate } from "react-router-dom";
import {
    SESSION_TYPE_PROJECT,
    SESSION_TYPE_CLEANING,
    SESSION_TYPE_CALLING,
    SESSION_TYPE_TECHNIK,
    SESSION_TYPE_NEW_LEAD,
    SESSION_TYPE_MARKETING,
    SESSION_TYPE_FREI_TEXT,
    SESSION_TYPE_AKQUISE,
    SESSION_TYPE_BUCHHALTUNG,
    SESSION_TYPE_BREAK,
    SESSION_TYPE_NO_PROJECT,
    SESSION_TYPE_NAMES
} from "../timeTracking";

const SESSION_TYPE_COLORS = {
    [SESSION_TYPE_PROJECT]: '#0025cc',
    [SESSION_TYPE_CLEANING]: '#8f1eae',
    [SESSION_TYPE_CALLING]: '#cca300',
    [SESSION_TYPE_TECHNIK]: '#4ca626',
    [SESSION_TYPE_NEW_LEAD]: '#1cabb0',
    [SESSION_TYPE_MARKETING]: '#ff5bc0',
    [SESSION_TYPE_FREI_TEXT]: '#ff751f',
    [SESSION_TYPE_AKQUISE]: '#059669',
    [SESSION_TYPE_BUCHHALTUNG]: '#475569',
    [SESSION_TYPE_BREAK]: '#cca300',
    [SESSION_TYPE_NO_PROJECT]: '#cc0000',
};

function SessionTable({ sessions, next, onEdit }) {
    const navigate = useNavigate();

    const selectProject = (sessionId) => {
        navigate(`/sessions/${sessionId}/select-project`);
    };

    const formatDateTime = (timestamp) => {
        return new Date(timestamp).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours}h ${minutes}m ${secs}s`;
    };

    const canEdit = (session) => {
        // Crewmeister breaks cannot be edited
        if (session.isBreakBooking) {
            return false;
        }

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return new Date(session.startTimestamp) >= sevenDaysAgo &&
            new Date(session.endTimestamp).getTime() !== 0;
    };

    return (
        <div className="bg-warmwhite rounded-xl shadow-sm border border-taupe/20 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="bg-lightcream border-b border-taupe/20">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-darkbrown/80 uppercase tracking-wider">
                                #
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-darkbrown/80 uppercase tracking-wider">
                                Start
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-darkbrown/80 uppercase tracking-wider">
                                Ende
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-darkbrown/80 uppercase tracking-wider">
                                Dauer
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-darkbrown/80 uppercase tracking-wider">
                                Typ
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-darkbrown/80 uppercase tracking-wider">
                                Projekt
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-darkbrown/80 uppercase tracking-wider">
                                Aktionen
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-taupe/10">
                        {sessions.map(session => (
                            <tr
                                key={session.id}
                                className="hover:bg-warmwhite transition-colors"
                            >
                                <td className="px-4 py-3 text-sm text-darkbrown/60 font-mono">
                                    {session.id}
                                </td>
                                <td className="px-4 py-3 text-sm text-darkbrown">
                                    {formatDateTime(session.startTimestamp)}
                                </td>
                                <td className="px-4 py-3 text-sm text-darkbrown">
                                    {new Date(session.endTimestamp).getTime() === 0 ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-olive/20 text-olive">
                                            <span className="material-symbols-outlined text-sm">play_arrow</span>
                                            Läuft
                                        </span>
                                    ) : (
                                        formatDateTime(session.endTimestamp)
                                    )}
                                </td>
                                <td className="px-4 py-3 text-sm text-darkbrown text-right">
                                    <div className="flex flex-col items-end">
                                        <span className="font-mono font-semibold">
                                            {formatDuration(session.seconds)}
                                        </span>
                                        {session.editStatus === 'PENDING_APPROVAL' && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gold bg-gold/10 px-1.5 py-0.5 rounded mt-1" title="Änderung wird geprüft">
                                                <span className="material-symbols-outlined text-[14px]">history</span>
                                                In Prüfung
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white shadow-sm"
                                            style={{ backgroundColor: SESSION_TYPE_COLORS[session.type] || '#475569' }}
                                        >
                                            {SESSION_TYPE_NAMES[session.type] || session.type}
                                        </span>
                                        {session.isBreakBooking && (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-darkbrown/10 text-darkbrown/70" title="Von Crewmeister synchronisiert">
                                                <i className="bi bi-cloud-check-fill"></i>
                                                Crewmeister
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-darkbrown">
                                    {session.type === 'PROJECT' && session.Project?.name ? (
                                        <div>
                                            <span className="font-medium">{session.Project.name}</span>
                                            {session.Subproject?.name && (
                                                <div className="text-xs text-darkbrown/60">→ {session.Subproject.name}</div>
                                            )}
                                        </div>
                                    ) : session.description ? (
                                        <span className="text-darkbrown/70 italic">{session.description}</span>
                                    ) : (
                                        <span className="text-darkbrown/40">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="flex items-center justify-end gap-2">
                                        {/* Edit Button - nur für Sessions der letzten 7 Tage */}
                                        {canEdit(session) && onEdit && (
                                            <button
                                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors text-xs font-medium ${session.editStatus === 'PENDING_APPROVAL'
                                                    ? 'bg-taupe/20 text-darkbrown/40 cursor-not-allowed'
                                                    : 'bg-terracotta text-white hover:bg-copper'
                                                    }`}
                                                onClick={() => session.editStatus !== 'PENDING_APPROVAL' && onEdit(session)}
                                                disabled={session.editStatus === 'PENDING_APPROVAL'}
                                                title={session.editStatus === 'PENDING_APPROVAL' ? 'Änderung wird bereits geprüft' : 'Session bearbeiten (nur letzte 7 Tage)'}
                                            >
                                                <i className={`bi bi-${session.editStatus === 'PENDING_APPROVAL' ? 'hourglass-split' : 'pencil-fill'}`}></i>
                                                {session.editStatus === 'PENDING_APPROVAL' ? 'In Prüfung...' : 'Bearbeiten'}
                                            </button>
                                        )}

                                        {/* Project Selection Button */}
                                        {(session.type === 'CALLING' || session.type === 'NEW_LEAD') &&
                                            new Date(session.endTimestamp).getTime() !== 0 && (
                                                <button
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-terracotta text-white hover:bg-copper transition-colors text-xs font-medium"
                                                    onClick={() => selectProject(session.id)}
                                                >
                                                    <span className="material-symbols-outlined text-base">folder</span>
                                                    Projekt wählen
                                                </button>
                                            )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Load More Button */}
            <div className="border-t border-taupe/20 p-4 bg-lightcream/50">
                <button
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-terracotta text-white hover:bg-copper transition-colors font-medium"
                    onClick={next}
                >
                    <span className="material-symbols-outlined">expand_more</span>
                    Mehr laden
                </button>
            </div>
        </div>
    );
}

export default SessionTable;